// File: src/lib/sync-queue.ts
// Simple retry queue with exponential backoff + deduplication by (unitId, sectionId).
// Stored in IndexedDB via idb-wrapper.
//
// FIELD NAME DISCIPLINE:
// ─────────────────────────────────────────────────────────────────────────────
// The retry timestamp field is `nextAttemptAt` (a millisecond epoch number).
// This is the only name used anywhere in this file. Do not introduce aliases
// like `nextAttemptAtMs` without updating all read sites.
//
// BUG 20 FIX — Dedup merge no longer resets deeply-retried items to attempt 0.
// ─────────────────────────────────────────────────────────────────────────────
// Old: attempt: Math.min(items[idx].attempt || 0, chosen.attempt || 0)
//
// Problem: if incoming.attempt = 0 and existing.attempt = 4, Math.min picks 0.
// This discards the item's full backoff history. The next failure after the
// dedup-merge would only back off ~2s instead of the correct ~5 min.
//
// Fix: always keep the existing item's attempt count. The `nextAttemptAt: 0`
// already schedules it to run immediately — the attempt counter's only job
// is to determine the NEXT backoff delay if that attempt also fails.

import { idbGet, idbSet, queueItemsKey, STORE_QUEUE } from './idb-wrapper';

// ── Public types ─────────────────────────────────────────────────────────────

export type QueueItemType = 'progress_write';

export interface QueueItem<TPayload extends Record<string, unknown> = Record<string, unknown>> {
  id: string;
  type: QueueItemType;
  unitId: string | null;
  sectionId: string | null;
  payload: TPayload;
  attempt: number;
  /** ms epoch — canonical field name. Do not alias. */
  nextAttemptAt: number;
  createdAt: number;
  lastError: string | null;
}

export interface EnqueueInput {
  id?: string;
  type: QueueItemType;
  unitId?: string | null;
  sectionId?: string | null;
  payload?: Record<string, unknown>;
  attempt?: number;
  nextAttemptAt?: number;
  createdAt?: number;
  lastError?: string | null;
}

export interface ProcessResult {
  processed: number;
  done: number;
  failed: number;
  remaining: number;
}

export interface ProcessOptions {
  maxItems?: number;
}

export type QueueHandler = (item: QueueItem) => Promise<void>;

// ── Helpers ──────────────────────────────────────────────────────────────────

const nowMs = (): number => Date.now();

function safeNum(x: unknown, d = 0): number {
  const n = Number(x);
  return Number.isFinite(n) ? n : d;
}

/**
 * Exponential backoff with ±20% jitter.
 * attempt=0 → ~2 s, attempt=1 → ~4 s, ..., capped at 5 min.
 */
function backoffMs(attempt: number, baseMs = 2000, maxMs = 5 * 60 * 1000): number {
  const raw = Math.min(maxMs, baseMs * Math.pow(2, Math.max(0, attempt)));
  const jitter = raw * 0.2;
  return Math.max(0, raw + (Math.random() * 2 * jitter - jitter));
}

function clone<T>(x: T): T {
  try {
    return structuredClone(x);
  } catch {
    return JSON.parse(JSON.stringify(x)) as T;
  }
}

// ── Class ────────────────────────────────────────────────────────────────────

export class SyncQueue {
  private readonly key: string;

  constructor() {
    // Single IDB key under which the entire queue array is stored.
    this.key = queueItemsKey(); // → 'queue:items'
  }

  // ── Storage helpers ────────────────────────────────────────────────────────

  async list(): Promise<QueueItem[]> {
    const v = await idbGet(this.key, STORE_QUEUE);
    return Array.isArray(v) ? (v as QueueItem[]) : [];
  }

  private async _save(items: QueueItem[]): Promise<void> {
    await idbSet(this.key, items, STORE_QUEUE);
  }

  // ── Deduplication ──────────────────────────────────────────────────────────

  /**
   * Returns the dedup key for a progress_write item, or null for other types.
   * Two items with the same (unitId, sectionId) are considered duplicates and
   * only the "better" one (higher version, or later timestamp) is kept.
   */
  private _dedupeKey(item: QueueItem | null | undefined): string | null {
    if (!item || item.type !== 'progress_write') return null;
    return `${item.unitId || ''}::${item.sectionId || ''}`;
  }

  /**
   * Given two queue items for the same (unitId, sectionId), returns whichever
   * carries more up-to-date progress data.
   * Priority: higher version number → if equal, more recent lastUpdatedMs.
   */
  private _chooseBetter(existing: QueueItem, incoming: QueueItem): QueueItem {
    const ePayload = (existing.payload || {}) as Record<string, unknown>;
    const iPayload = (incoming.payload || {}) as Record<string, unknown>;

    const eSec =
      (existing.sectionId && (ePayload[existing.sectionId] as Record<string, unknown> | undefined)) ||
      (ePayload.progress as Record<string, unknown> | undefined) ||
      {};
    const iSec =
      (incoming.sectionId && (iPayload[incoming.sectionId] as Record<string, unknown> | undefined)) ||
      (iPayload.progress as Record<string, unknown> | undefined) ||
      {};

    const ev = safeNum(eSec.version);
    const iv = safeNum(iSec.version);
    if (iv > ev) return incoming;
    if (iv < ev) return existing;

    const et = safeNum(eSec.lastUpdatedMs);
    const it = safeNum(iSec.lastUpdatedMs);
    return it >= et ? incoming : existing;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Add an item to the queue.
   * If an item with the same (unitId, sectionId) already exists, the "better"
   * one is kept and its retry schedule is reset so it's tried sooner.
   */
  async enqueue(input: EnqueueInput): Promise<QueueItem> {
    const items = await this.list();
    const normalized: QueueItem = {
      id: input.id || `${nowMs()}-${Math.random().toString(16).slice(2)}`,
      type: input.type,
      unitId: input.unitId ?? null,
      sectionId: input.sectionId ?? null,
      payload: clone(input.payload || {}),
      attempt: safeNum(input.attempt, 0),
      nextAttemptAt: safeNum(input.nextAttemptAt, 0), // canonical field name
      createdAt: safeNum(input.createdAt, nowMs()),
      lastError: input.lastError ?? null,
    };

    const dk = this._dedupeKey(normalized);
    if (dk) {
      const idx = items.findIndex(x => this._dedupeKey(x) === dk);
      if (idx >= 0) {
        const chosen = this._chooseBetter(items[idx], normalized);
        items[idx] = {
          ...chosen,
          // BUG 20 FIX: Preserve the existing item's attempt count.
          //
          // Old code used Math.min(existing.attempt, incoming.attempt).
          // If incoming.attempt = 0 and existing.attempt = 4, Math.min = 0,
          // which silently discards 4 rounds of backoff history. The next
          // failure after the merge would back off only ~2s instead of ~5min.
          //
          // We always keep items[idx].attempt (the existing depth), regardless
          // of which item's payload won. The `nextAttemptAt: 0` already
          // ensures the merged item is retried immediately. The attempt counter
          // only matters if THAT retry also fails — in which case we want to
          // continue from the existing backoff depth, not restart from zero.
          attempt: safeNum(items[idx].attempt, 0),
          nextAttemptAt: 0, // due immediately
          lastError: null,
        };
        await this._save(items);
        return items[idx];
      }
    }

    items.push(normalized);
    await this._save(items);
    return normalized;
  }

  /** Remove a successfully processed item from the queue. */
  async markDone(id: string): Promise<void> {
    const items = await this.list();
    await this._save(items.filter(x => x.id !== id));
  }

  /** Update a queue item in-place (e.g. after a failed attempt). */
  async update(item: QueueItem): Promise<QueueItem | null> {
    const items = await this.list();
    const idx = items.findIndex(x => x.id === item.id);
    if (idx >= 0) {
      items[idx] = { ...items[idx], ...clone(item) };
      await this._save(items);
      return items[idx];
    }
    return null;
  }

  /**
   * Process due items by calling `handler(item)` for each one.
   *
   * An item is "due" when `nextAttemptAt <= Date.now()`.
   * On success → item is removed via markDone().
   * On failure → attempt counter incremented, `nextAttemptAt` set to
   *              now + backoff(attempt), `lastError` recorded.
   */
  async process(handler: QueueHandler, options: ProcessOptions = {}): Promise<ProcessResult> {
    const maxItems = options.maxItems ?? 10;
    const items = await this.list();

    // Filter to items whose backoff delay has elapsed.
    // Uses `nextAttemptAt` — the single canonical field name for this timestamp.
    const due = items
      .filter(x => safeNum(x.nextAttemptAt) <= nowMs())
      .slice(0, maxItems);

    let done = 0;
    let failed = 0;

    for (const item of due) {
      try {
        await handler(item);
        await this.markDone(item.id);
        done++;
      } catch (err) {
        failed++;
        const attempt = safeNum(item.attempt, 0) + 1;
        const errMsg = err instanceof Error
          ? err.message
          : err
            ? String(err)
            : 'unknown error';
        await this.update({
          ...item,
          attempt,
          nextAttemptAt: nowMs() + backoffMs(attempt), // same field name
          lastError: errMsg,
        });
      }
    }

    const remaining = (await this.list()).length;
    return { processed: due.length, done, failed, remaining };
  }

  /** Returns the number of items currently in the queue (pending + deferred). */
  async count(): Promise<number> {
    return (await this.list()).length;
  }

  /** Returns only items that are immediately due (nextAttemptAt <= now). */
  async countDue(): Promise<number> {
    const items = await this.list();
    const now = nowMs();
    return items.filter(x => safeNum(x.nextAttemptAt) <= now).length;
  }
}